import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { Badge } from "../badge";
import { formatCalendarDate } from "../../utils/dates";
import { esItemizado, esPool, unidadDe } from "../../utils/catalogo";
import ServicioCalendarioUso from "./ServicioCalendarioUso";
import { ESTADO_UNIDAD_LABELS } from "../../utils/inventario";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";
const ESTADO_COLOR = { activo: "emerald", mantenimiento: "amber", baja: "zinc" };
const MAX_PASADAS = 10;

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

const Card = ({ title, children }) => (
  <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/50 p-5 shadow-sm">
    <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500 mb-3">
      {title}
    </div>
    {children}
  </div>
);

// Agenda de reservas de un servicio: qué eventos lo tienen asignado, qué
// piezas se lleva cada uno y cómo queda la disponibilidad por pieza.
// Todo sale de `recursosAsignados` denormalizado en los eventos que
// VenueProvider ya tiene suscritos — cero queries extra (ver
// utils/unidades.js para el porqué de la denormalización).
const ServicioReservas = ({ item, venueId }) => {
  const navigate = useNavigate();
  const { events, calendarTimezone } = useVenue();

  const itemizado = esItemizado(item);
  const [unidades, setUnidades] = useState([]);

  useEffect(() => {
    if (!venueId || !item?.id || !itemizado) {
      setUnidades([]);
      return;
    }
    const unsub = onSnapshot(
      collection(db, "venues", venueId, "conceptos", item.id, "unidades"),
      (snap) =>
        setUnidades(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .sort((a, b) =>
              (a.etiqueta || "").localeCompare(b.etiqueta || "", "es", { numeric: true })
            )
        ),
      (err) => console.error("Unidades snapshot error:", err)
    );
    return unsub;
  }, [venueId, item?.id, itemizado]);

  const unidadesPorId = useMemo(() => {
    const map = {};
    unidades.forEach((u) => { map[u.id] = u; });
    return map;
  }, [unidades]);

  // Un evento cancelado libera sus piezas, así que no cuenta como reserva
  // — el mismo criterio que el bloqueo duro de utils/unidades.js.
  const reservas = useMemo(() => {
    return (events || [])
      .filter((ev) => ev && ev.status !== "cancelado")
      .map((ev) => {
        const asignacion = (Array.isArray(ev.recursosAsignados)
          ? ev.recursosAsignados
          : []
        ).find((a) => a?.recursoId === item.id);
        if (!asignacion) return null;
        const inicio = toDate(ev.startTime);
        if (!inicio) return null;
        return {
          evento: ev,
          asignacion,
          inicio,
          fin: toDate(ev.endTime) || inicio,
        };
      })
      .filter(Boolean);
  }, [events, item.id]);

  const ahora = new Date();
  const proximas = reservas
    .filter((r) => r.fin >= ahora)
    .sort((a, b) => a.inicio - b.inicio);
  const pasadas = reservas
    .filter((r) => r.fin < ahora)
    .sort((a, b) => b.inicio - a.inicio);

  const fmtFecha = (r) =>
    formatCalendarDate(r.evento.startTime, calendarTimezone, {
      style: "short",
      weekday: "short",
      instante: true,
    }) || "—";

  const linkEvento = (ev, className) => (
    <button
      type="button"
      onClick={() => navigate(`/dashboard/eventos/${ev.id}`)}
      className={`hover:underline text-left ${className || ""}`}
      style={{ color: ACCENT }}
    >
      {ev.nombre || "Sin nombre"}
    </button>
  );

  const chipsUnidades = (asignacion) => {
    const ids = Array.isArray(asignacion?.unidadIds) ? asignacion.unidadIds : [];
    if (ids.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1">
        {ids.map((id) => (
          <span
            key={id}
            className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-700/60 px-1.5 py-0.5 text-xs font-medium text-zinc-700 dark:text-zinc-200"
          >
            {unidadesPorId[id]?.etiqueta || "Pieza eliminada"}
          </span>
        ))}
      </div>
    );
  };

  const filaReserva = (r, muted = false) => {
    const enCurso = r.inicio <= ahora && r.fin >= ahora;
    const cantidad = Number(r.asignacion?.cantidad) || 0;
    return (
      <tr key={r.evento.id} className={muted ? "opacity-60" : undefined}>
        <td className="px-2 py-2 text-zinc-500 whitespace-nowrap w-36">
          {fmtFecha(r)}
        </td>
        <td className="px-2 py-2">
          {linkEvento(r.evento, "text-sm font-medium")}
          {enCurso && (
            <span className="ml-2 align-middle">
              <Badge color="emerald">Hoy</Badge>
            </span>
          )}
        </td>
        <td className="px-2 py-2">
          {chipsUnidades(r.asignacion) ||
            (cantidad > 0 ? (
              <span className="text-xs text-zinc-500">
                {cantidad} {unidadDe(item)}
              </span>
            ) : (
              <span className="text-xs text-zinc-400">—</span>
            ))}
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-5">
      {/* Disponibilidad por pieza — el "booking board". Solo en identificadas:
          por cantidad no hay pieza que pintar, y el calendario de arriba ya
          dice cuántas quedan cada día. */}
      {itemizado && !esPool(item) && unidades.length > 0 && (
        <Card title="Disponibilidad por pieza">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {unidades.map((u) => {
              const estado = u.estado || "activo";
              const deUnidad = proximas.filter((r) =>
                (r.asignacion.unidadIds || []).includes(u.id)
              );
              const actual = deUnidad.find(
                (r) => r.inicio <= ahora && r.fin >= ahora
              );
              const siguiente = deUnidad.find((r) => r.inicio > ahora);
              return (
                <div
                  key={u.id}
                  className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 p-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-zinc-900 dark:text-white truncate">
                      {u.etiqueta}
                    </div>
                    {/* Badge solo cuando dice algo: en uso hoy, mantenimiento
                        o baja. Una pieza activa sin evento en curso no
                        necesita etiqueta — "Libre" junto a "Próxima reserva"
                        sonaba a que no tenía reservas. */}
                    {estado !== "activo" ? (
                      <Badge color={ESTADO_COLOR[estado]}>
                        {ESTADO_UNIDAD_LABELS[estado]}
                      </Badge>
                    ) : actual ? (
                      <Badge color="blue">En uso</Badge>
                    ) : null}
                  </div>
                  <div className="mt-2 text-xs text-zinc-500">
                    {actual ? (
                      <>Hoy en {linkEvento(actual.evento, "text-xs font-medium")}</>
                    ) : siguiente ? (
                      <>
                        Próxima: {fmtFecha(siguiente)} ·{" "}
                        {linkEvento(siguiente.evento, "text-xs font-medium")}
                      </>
                    ) : (
                      "Sin reservas próximas"
                    )}
                  </div>
                  {deUnidad.length > 1 && (
                    <div className="mt-1 text-xs text-zinc-400">
                      {deUnidad.length} reservas por venir
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Próximas reservas */}
      <Card title="Próximas reservas">
        {proximas.length === 0 ? (
          <div className="py-6 text-center">
            <CalendarDaysIcon className="mx-auto h-8 w-8 text-zinc-300" />
            <p className="mt-2 text-sm text-zinc-400">
              Ningún evento por venir tiene este servicio asignado. Las
              piezas se apartan desde la sección Servicios de cada evento.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {proximas.map((r) => filaReserva(r))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Historial */}
      {pasadas.length > 0 && (
        <Card title={`Reservas pasadas (${pasadas.length})`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <tbody className="divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {pasadas.slice(0, MAX_PASADAS).map((r) => filaReserva(r, true))}
              </tbody>
            </table>
          </div>
          {pasadas.length > MAX_PASADAS && (
            <p className="mt-2 text-xs text-zinc-400">
              …y {pasadas.length - MAX_PASADAS} más antiguas.
            </p>
          )}
        </Card>
      )}

      {/* Uso mes por mes, al final: es el resumen de todo lo de arriba, no el
          punto de entrada. */}
      {itemizado && (
        <ServicioCalendarioUso item={item} reservas={reservas} unidades={unidades} />
      )}
    </div>
  );
};

export default ServicioReservas;
